import React, { Component, ErrorInfo, ReactNode } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

const ErrorContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  background: ${props => props.theme?.colors?.surface || '#f8fafc'};
  border-radius: 12px;
  margin: 20px;
  border: 1px solid ${props => props.theme?.colors?.border || '#e2e8f0'};
`;

const ErrorIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 20px;
  color: ${props => props.theme?.colors?.error || '#ef4444'};
`;

const ErrorTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme?.colors?.text || '#1e293b'};
  margin-bottom: 12px;
`;

const ErrorMessage = styled.p`
  font-size: 1rem;
  color: ${props => props.theme?.colors?.textSecondary || '#64748b'};
  margin-bottom: 24px;
  max-width: 500px;
  line-height: 1.5;
`;

const RetryButton = styled.button`
  background: ${props => props.theme?.colors?.primary || '#6366f1'};
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme?.colors?.primaryDark || '#4f46e5'};
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const ErrorDetails = styled.details`
  margin-top: 20px;
  padding: 16px;
  background: ${props => props.theme?.colors?.background || '#ffffff'};
  border-radius: 8px;
  border: 1px solid ${props => props.theme?.colors?.border || '#e2e8f0'};
  max-width: 600px;
  
  summary {
    cursor: pointer;
    font-weight: 500;
    color: ${props => props.theme?.colors?.textSecondary || '#64748b'};
    margin-bottom: 8px;
  }
  
  pre {
    font-size: 0.8rem;
    color: ${props => props.theme?.colors?.error || '#ef4444'};
    background: ${props => props.theme?.colors?.surface || '#f8fafc'};
    padding: 12px;
    border-radius: 4px;
    overflow-x: auto;
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // You can log the error to an error reporting service here
    // Example: logErrorToService(error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorContainer
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <ErrorIcon>⚠️</ErrorIcon>
          <ErrorTitle>Oops! Something went wrong</ErrorTitle>
          <ErrorMessage>
            We encountered an unexpected error while loading this section. 
            Please try refreshing the page or contact support if the problem persists.
          </ErrorMessage>
          <RetryButton onClick={this.handleRetry}>
            Try Again
          </RetryButton>
          
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <ErrorDetails>
              <summary>Error Details (Development Only)</summary>
              <pre>{this.state.error.stack}</pre>
            </ErrorDetails>
          )}
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;